import { LABEL_FONT } from './network/constants';
import { labelBox } from './display';
import { parseMath, typeset } from './math';

/** Plain-text line box in em: baseline sits 0.95em below the top, 0.25em above the bottom (1.2em total). */
export const TEXT_ABOVE = 0.95;
const TEXT_BELOW = 0.25;
/** Clearance kept around tall maths (fractions, stacked scripts). */
const MATH_PAD = 0.05;

const estimateWidth = (text, fontSize) => [...String(text)].length * fontSize * 0.55;

let context;
/** Text width in px, from a canvas when there is one; a character estimate otherwise (tests). */
export const measureText = (text, fontSize) => {
  if (context === undefined) {
    context = null;
    const isJsdom = typeof navigator !== 'undefined' && /jsdom/.test(navigator.userAgent);
    if (typeof document !== 'undefined' && !isJsdom) {
      context = document.createElement('canvas').getContext('2d') || null;
    }
  }
  if (!context) return estimateWidth(text, fontSize);
  context.font = `${fontSize}px ${LABEL_FONT.family}`;
  return context.measureText(text).width;
};

/** Words of a paragraph: runs of text and typeset maths not separated by whitespace. */
const wordsOf = (paragraph) => {
  const words = [];
  let current = [];
  const flush = () => {
    if (current.length) words.push(current);
    current = [];
  };
  parseMath(paragraph).forEach((part) => {
    if (part.type === 'math') {
      const math = typeset(part.value);
      current.push(math ? { type: 'math', value: part.value, math } : { type: 'text', value: `$${part.value}$` });
      return;
    }
    part.value.split(/(\s+)/).forEach((chunk, index) => {
      if (index % 2 === 1) flush();
      else if (chunk) current.push({ type: 'text', value: chunk });
    });
  });
  flush();
  return words;
};

/** Merge neighbouring text pieces so each run is drawn, and measured, as one string. */
const joinPieces = (pieces) => pieces.reduce((acc, piece) => {
  const prev = acc[acc.length - 1];
  if (piece.type === 'text' && prev?.type === 'text') {
    return [...acc.slice(0, -1), { type: 'text', value: prev.value + piece.value }];
  }
  return [...acc, piece];
}, []);

const pieceWidth = (piece, fontSize, measure) => (
  piece.type === 'math' ? piece.math.width * fontSize : measure(piece.value, fontSize)
);

const buildLine = (pieces, fontSize, measure) => {
  let x = 0;
  let above = TEXT_ABOVE * fontSize;
  let below = TEXT_BELOW * fontSize;
  const placed = joinPieces(pieces).map((piece) => {
    const width = pieceWidth(piece, fontSize, measure);
    if (piece.type === 'math') {
      above = Math.max(above, (piece.math.height + MATH_PAD) * fontSize);
      below = Math.max(below, (piece.math.depth + MATH_PAD) * fontSize);
    }
    const out = { ...piece, x, width };
    x += width;
    return out;
  });
  return {
    pieces: placed, width: x, above, below,
  };
};

/**
 * Lay out a label that mixes text and `$...$` maths. Wraps at whitespace to `maxWidth`
 * (explicit newlines always break). Returns lines with pieces positioned from each line's left,
 * plus each line's extent above and below its baseline, all in px.
 */
export const layoutRichLabel = (text, maxWidth, fontSize, measure = measureText) => {
  const lines = [];
  String(text ?? '').split('\n').forEach((paragraph) => {
    const words = wordsOf(paragraph);
    if (!words.length) {
      lines.push(buildLine([], fontSize, measure));
      return;
    }
    const space = measure(' ', fontSize);
    let current = [];
    let width = 0;
    words.forEach((word) => {
      const wordWidth = word.reduce((sum, piece) => sum + pieceWidth(piece, fontSize, measure), 0);
      if (current.length && width + space + wordWidth > maxWidth) {
        lines.push(buildLine(current, fontSize, measure));
        current = [];
        width = 0;
      }
      if (current.length) {
        current.push({ type: 'text', value: ' ' });
        width += space;
      }
      current.push(...word);
      width += wordWidth;
    });
    lines.push(buildLine(current, fontSize, measure));
  });
  const height = lines.reduce((sum, line) => sum + line.above + line.below, 0);
  const width = Math.max(0, ...lines.map((line) => line.width));
  return { lines, width, height };
};

/** Largest font size (down to `minSize`) at which the rich label fits inside the shape. */
export const fitRichLabel = (text, shape, size, baseSize = LABEL_FONT.size, minSize = 10) => {
  const box = labelBox(shape, size);
  let fontSize = baseSize;
  let layout = layoutRichLabel(text, box.width, fontSize);
  while (fontSize > minSize && (layout.height > box.height || layout.width > box.width)) {
    fontSize -= 1;
    layout = layoutRichLabel(text, box.width, fontSize);
  }
  return { fontSize, ...layout };
};
